// emailTemplates.js — the supplier-facing emails Floxir drafts, in the language the
// workspace is actually using.
//
// WHY THIS EXISTS. Every drafted email was hardcoded Italian — the correction request,
// the chaser and the reorder purchase order alike — regardless of the app's language.
// An English workspace opened a case, pressed "Request correction", and was handed
// "Buongiorno, … Cordiali saluti, Acquisti" to send to a British mill.
//
// One module for both languages so a change to what an email SAYS cannot land in one
// language and not the other. The templates deliberately mirror each other line for
// line; the Italian keeps its courtesy formulas, which are standard in Italian
// commercial correspondence and read as curt if dropped.

const money = (n, sym) => (n == null ? null : `${sym}${Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

// ── the problem, stated in one line ─────────────────────────────────────────
// Keyed by the matching engine's own discrepancy type, so a new type is a visible
// gap here rather than a silent fallthrough into vague wording.
const ISSUE = {
  en: {
    price_higher: 'a unit price above the one agreed on the order.',
    extra_charge: 'a charge that was not on the order.',
    vat_error: 'a VAT rate that does not match what was agreed.',
    duplicate: 'an invoice that appears to duplicate one already received.',
    duplicate_invoice: 'an invoice that appears to duplicate one already received.',
    qty_short: 'a delivered quantity lower than the quantity invoiced.',
    qty_over: 'a delivered quantity higher than the quantity ordered.',
    billed_over_delivered: 'an invoiced quantity higher than the quantity delivered.',
    missing_po: 'that we cannot match this invoice to one of our orders.',
    late_delivery: 'that the delivery has not arrived within the agreed time.',
    _default: 'a difference we would like to clarify before payment.',
  },
  it: {
    price_higher: "un prezzo unitario superiore a quello concordato nell'ordine.",
    extra_charge: "una voce aggiuntiva non prevista nell'ordine.",
    vat_error: "un'aliquota IVA che non corrisponde a quanto concordato.",
    duplicate: 'una fattura che risulta duplicato di una già ricevuta.',
    duplicate_invoice: 'una fattura che risulta duplicato di una già ricevuta.',
    qty_short: 'una quantità consegnata inferiore a quella fatturata.',
    qty_over: "una quantità consegnata superiore a quella ordinata.",
    billed_over_delivered: 'una quantità fatturata superiore a quella consegnata.',
    missing_po: 'che non risulta possibile associare la fattura a un nostro ordine.',
    late_delivery: 'che la consegna non è pervenuta nei tempi concordati.',
    _default: 'una differenza che desideriamo chiarire prima del pagamento.',
  },
};

// The supporting figure, where the engine recorded one.
function detailLine(lang, type, amount, quantity, sym) {
  const amt = money(amount, sym);
  if (lang === 'it') {
    if ((type === 'price_higher' || type === 'extra_charge') && amt) return `Differenza rilevata: ${amt}.`;
    if (type === 'qty_short' && quantity != null) return `Mancano ${quantity} pezzi rispetto alla fattura.`;
    if (type === 'qty_over' && quantity != null) return `Eccedenza: ${quantity} pezzi oltre l'ordine.`;
    if (type === 'billed_over_delivered' && quantity != null) return `Fatturati ${quantity} pezzi in più rispetto alla merce consegnata.`;
    return null;
  }
  if ((type === 'price_higher' || type === 'extra_charge') && amt) return `Difference identified: ${amt}.`;
  if (type === 'qty_short' && quantity != null) return `${quantity} units short of the invoiced quantity.`;
  if (type === 'qty_over' && quantity != null) return `${quantity} units in excess of the order.`;
  if (type === 'billed_over_delivered' && quantity != null) return `${quantity} units invoiced in excess of those delivered.`;
  return null;
}

// ── correction request ──────────────────────────────────────────────────────
export function correctionDraft(c, { lang = 'en', currencySymbol = '' } = {}) {
  const l = lang === 'it' ? 'it' : 'en';
  const allInv = Array.isArray(c?.invoices) && c.invoices.length > 0 ? c.invoices : c?.invoice ? [c.invoice] : [];
  const numbers = allInv.map((i) => i.invoice_number).filter(Boolean);
  const primary = numbers[numbers.length - 1] || '—';
  const listed = numbers.length > 1 ? numbers.join(', ') : primary;
  const po = c?.purchase_orders?.po_number || null;
  const issue = ISSUE[l][c?.discrepancy_type] || ISSUE[l]._default;
  const detail = detailLine(l, c?.discrepancy_type, c?.discrepancy_amount, c?.discrepancy_quantity, currencySymbol);
  const many = numbers.length > 1;

  if (l === 'it') {
    const subject = many
      ? `Richiesta correzione fatture${po ? ` – ordine ${po}` : ''}`
      : `Richiesta correzione fattura ${primary}${po ? ` – ordine ${po}` : ''}`;
    const body = [
      'Buongiorno,',
      '',
      `abbiamo ricevuto ${many ? `le fatture ${listed}` : `la fattura ${primary}`}${po ? ` relative all'ordine ${po}` : ''}, e riscontriamo ${issue}`,
      detail ? '' : null, detail || null,
      '',
      'Vi chiediamo cortesemente di emettere una nota di credito o una fattura corretta.',
      '',
      'In attesa di un vostro riscontro, cordiali saluti.',
      '',
      'Ufficio Acquisti',
    ].filter((x) => x !== null).join('\n');
    return { to: c?.suppliers?.email || '', subject, body };
  }

  const subject = many
    ? `Invoice correction requested${po ? ` – order ${po}` : ''}`
    : `Invoice ${primary} – correction requested${po ? ` – order ${po}` : ''}`;
  const body = [
    'Dear Sir or Madam,',
    '',
    `We have received ${many ? `invoices ${listed}` : `invoice ${primary}`}${po ? ` against order ${po}` : ''}, and have identified ${issue}`,
    detail ? '' : null, detail || null,
    '',
    'Please issue a credit note or a corrected invoice.',
    '',
    'We look forward to your response.',
    '',
    'Purchasing',
  ].filter((x) => x !== null).join('\n');
  return { to: c?.suppliers?.email || '', subject, body };
}

// ── reorder purchase order ──────────────────────────────────────────────────
export function purchaseOrderDraft({ lang = 'en', supplierName, poNumber, itemLines, total, currency, dateLong }) {
  const l = lang === 'it' ? 'it' : 'en';
  if (l === 'it') {
    return {
      subject: `Nuovo ordine di acquisto ${poNumber} · ${dateLong}`,
      body: [
        `Buongiorno${supplierName ? ` ${supplierName}` : ''},`,
        '',
        `vi inviamo il nostro ordine di acquisto ${poNumber} per i seguenti articoli:`,
        '', itemLines, '',
        total > 0 ? `Totale: ${Number(total).toFixed(2)} ${currency}` : null,
        total > 0 ? '' : null,
        "Vi chiediamo cortesemente conferma dell'ordine e dei tempi di consegna previsti.",
        '', 'Cordiali saluti,', 'Ufficio Acquisti',
      ].filter((x) => x !== null).join('\n'),
    };
  }
  return {
    subject: `Purchase order ${poNumber} · ${dateLong}`,
    body: [
      `Dear${supplierName ? ` ${supplierName}` : ' Sir or Madam'},`,
      '',
      `Please find our purchase order ${poNumber} for the following items:`,
      '', itemLines, '',
      total > 0 ? `Total: ${Number(total).toFixed(2)} ${currency}` : null,
      total > 0 ? '' : null,
      'Please confirm the order and the expected delivery date.',
      '', 'Kind regards,', 'Purchasing',
    ].filter((x) => x !== null).join('\n'),
  };
}

// ── follow-up on an unanswered correction request ───────────────────────────
// The SECOND email about a case. Pressing "Send follow-up" used to re-send the
// correction request word for word, so the supplier received the same message twice
// with nothing to say a reply was already overdue. This one says it is a reminder,
// names the date of the first request, and restates the problem in one line rather
// than re-arguing it.
//
// `contactedOn` is a display-ready date string (the caller formats it — this module
// has no opinion on date format) or null when the send date isn't known.
export function chaseDraft(c, { lang = 'en', currencySymbol = '', contactedOn = null } = {}) {
  const l = lang === 'it' ? 'it' : 'en';
  const first = correctionDraft(c, { lang: l, currencySymbol });
  const allInv = Array.isArray(c?.invoices) && c.invoices.length > 0 ? c.invoices : c?.invoice ? [c.invoice] : [];
  const numbers = allInv.map((i) => i.invoice_number).filter(Boolean);
  const primary = numbers[numbers.length - 1] || '—';
  const po = c?.purchase_orders?.po_number || null;
  const issue = ISSUE[l][c?.discrepancy_type] || ISSUE[l]._default;
  const many = numbers.length > 1;

  if (l === 'it') {
    return {
      to: c?.suppliers?.email || '',
      subject: `Sollecito – ${first.subject}`,
      body: [
        'Buongiorno,',
        '',
        `facciamo seguito alla nostra richiesta${contactedOn ? ` del ${contactedOn}` : ''} relativa ${many ? `alle fatture ${numbers.join(', ')}` : `alla fattura ${primary}`}${po ? ` – ordine ${po}` : ''}, alla quale non abbiamo ancora ricevuto riscontro.`,
        '',
        `Ricordiamo che risulta ${issue}`,
        '',
        'Vi chiediamo cortesemente di emettere una nota di credito o una fattura corretta, oppure di indicarci a chi possiamo rivolgerci.',
        '',
        'In attesa di un vostro riscontro, cordiali saluti.',
        '',
        'Ufficio Acquisti',
      ].join('\n'),
    };
  }
  return {
    to: c?.suppliers?.email || '',
    subject: `Reminder – ${first.subject}`,
    body: [
      'Dear Sir or Madam,',
      '',
      `We are following up on our request${contactedOn ? ` of ${contactedOn}` : ''} regarding ${many ? `invoices ${numbers.join(', ')}` : `invoice ${primary}`}${po ? ` – order ${po}` : ''}, to which we have not yet had a reply.`,
      '',
      `To recap, we have identified ${issue}`,
      '',
      'Please issue a credit note or a corrected invoice, or tell us who we should be speaking to.',
      '',
      'We look forward to your response.',
      '',
      'Purchasing',
    ].join('\n'),
  };
}

// The item line on a purchase order — the unit abbreviation is language-specific.
export function poItemLine({ lang = 'en', description, sku, qty }) {
  const unit = lang === 'it' ? 'pz' : 'units';
  return `- ${description}${sku ? ` (${sku})` : ''}: ${qty} ${unit}`;
}
